export const DEFAULT_TIME_ZONE = 'Europe/London'

export function activeTimeZone(timeZone) {
  const zone = timeZone || DEFAULT_TIME_ZONE
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone: zone })
    return zone
  } catch {
    return DEFAULT_TIME_ZONE
  }
}

/**
 * Composable for date and time formatting utilities
 * @returns {Object} Object containing formatDateTime and other date formatting functions
 */
export function useDateTime() {
  /**
   * Format a date-time string to a readable format
   * @param {string} dateTimeString - The date-time string to format
   * @param {Object} options - Optional formatting options
   * @returns {string} Formatted date-time string (e.g., "12 Jan 2026 11:54 AM")
   */
  const formatDateTime = (dateTimeString, options = {}) => {
    if (!dateTimeString) return ''
    
    const defaultOptions = {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: activeTimeZone(),
    }
    
    const formatOptions = { ...defaultOptions, ...options }
    return new Date(dateTimeString).toLocaleDateString('en-GB', formatOptions)
  }

  /**
   * Format a date-time string to a short format (date only)
   * @param {string} dateTimeString - The date-time string to format
   * @returns {string} Formatted date string (e.g., "12 Jan 2026")
   */
  const formatDate = (dateTimeString) => {
    if (!dateTimeString) return ''
    
    const options = {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: activeTimeZone()
    }
    return new Date(dateTimeString).toLocaleDateString('en-GB', options)
  }

  /**
   * Format a date-time string to time only
   * @param {string} dateTimeString - The date-time string to format
   * @returns {string} Formatted time string (e.g., "11:54 AM")
   */
  const formatTime = (dateTimeString) => {
    if (!dateTimeString) return ''
    
    const options = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: activeTimeZone()
    }
    return new Date(dateTimeString).toLocaleTimeString('en-GB', options)
  }

  /**
   * Format a date to YYYY-MM-DD format (for form inputs)
   * @param {Date|string} date - The date to format
   * @returns {string} Formatted date string (e.g., "2026-01-12")
   */
  const formatDateForInput = (date) => {
    if (!date) return ''
    const dateObj = date instanceof Date ? date : new Date(date)
    const year = dateObj.getFullYear()
    const month = String(dateObj.getMonth() + 1).padStart(2, '0')
    const day = String(dateObj.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  return {
    activeTimeZone,
    formatDateTime,
    formatDate,
    formatTime,
    formatDateForInput
  }
}
